import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Container_v2 as Container,
  ContractTreeFeature,
  getSignAgreementsTabs,
  Spinner_v2 as Spinner,
  TabItem,
  Tabs,
  TreeNode,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { fetchSpecialistContractJsonTreeById, fetchSpecialistContractNewJsonTreeById } from '../../api/contracts';
import useToast from '../../context/ToastContext/ToastContext';

type SignAgreementWidgetProps = {
  contractStatus: 'not_signed' | 'pending' | 'active';
  type: 'new' | 'last';
  isLoading?: boolean;
  specialistId: string;
  onApply: () => void;
};

export function SignAgreementWidget({
  contractStatus,
  onApply,
  specialistId,
  type = 'new',
  isLoading = false,
}: SignAgreementWidgetProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation('schedule');
  const { showToast } = useToast();
  const [fetchStatus, setFetchStatus] = useState<'idle' | 'pending' | 'error' | 'success'>('idle');
  const [jsonTree, setJsonTree] = useState<TreeNode[][]>([]);

  useEffect(() => {
    async function fetchJsonData() {
      try {
        setFetchStatus('pending');
        const contracts = getSignAgreementsTabs({
          jsonTree: [],
          localeOrCountry: language,
          behavior: 'profile-lang',
          t,
        });

        const data = await Promise.all(
          contracts?.map((contract) =>
            type === 'new'
              ? fetchSpecialistContractNewJsonTreeById(specialistId, contract.id)
              : fetchSpecialistContractJsonTreeById(specialistId, contract.id)
          )
        );

        setJsonTree(data);
        setFetchStatus('success');
      } catch (error) {
        setFetchStatus('error');
        showToast({ error: Array.isArray(error) ? error?.[0]?.err : error });
      }
    }

    fetchJsonData();
  }, [language, specialistId]);

  const tabs = useMemo(
    () =>
      getSignAgreementsTabs({
        behavior: 'profile-lang',
        localeOrCountry: language,
        jsonTree,
        t,
      }),
    [jsonTree, language, t]
  );

  return fetchStatus === 'pending' ? (
    <Container isFlexContainer>
      <div className={'flex flex-1 items-center justify-center'}>
        <Spinner size={48} />
      </div>
    </Container>
  ) : (
    <>
      <Container>
        <Typography variant={TypographyVariantsEnum.Title24Semi}>{t('agreement.title')}</Typography>
        <Tabs items={tabs}>
          {(tab) => <TabItem title={tab.title}>{tab.value ? <ContractTreeFeature data={tab.value} /> : null}</TabItem>}
        </Tabs>
      </Container>
      <AppFooter>
        {contractStatus === 'not_signed' && (
          <Button size="large" fullWidth onClick={onApply} isLoading={isLoading}>
            {t('agreement.confirm_button')}
          </Button>
        )}
        {contractStatus === 'pending' && (
          <Typography
            variant={TypographyVariantsEnum.Text13Regular}
            align="center"
            className="!text-typography-typography-primary/50"
          >
            {t('agreement.on_specialist_sign_hint')}
          </Typography>
        )}
      </AppFooter>
    </>
  );
}
